import { useState, useMemo, useEffect, useCallback } from 'react';

// Firebase Config & Firestore SDK
import { db } from '../../../../firebase/config'; 
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';

// Utilidades centralizadas del proyecto
import { parseCurrency, formatDateForInput, parseDateToComparable } from '../../../../utils/segurosUtils';
import { useSegurosData } from '../../../../context/SegurosDataContext';
import { useReportesMetrics } from '../../../../context/ReportesMetricsContext';

// TIEMPO DE VIDA DE LA CACHÉ EN RAM: 3 HORAS
const CACHE_TTL_MS = 1000 * 60 * 60 * 3;

// CONFIGURACIÓN TRIMESTRAL COMERCIAL SANCOR
export const TRIMESTRES_CONFIG = [
    { id: 'Q1', label: '1° Trimestre', sublabel: 'Julio 2026 – Septiembre 2026', mesInicio: '07', diaInicio: '01', mesFin: '09', diaFin: '30' },
    { id: 'Q2', label: '2° Trimestre', sublabel: 'Octubre 2026 – Diciembre 2026', mesInicio: '10', diaInicio: '01', mesFin: '12', diaFin: '31' },
    { id: 'Q3', label: '3° Trimestre', sublabel: 'Enero 2027 – Marzo 2027', mesInicio: '01', diaInicio: '01', mesFin: '03', diaFin: '31' },
    { id: 'Q4', label: '4° Trimestre', sublabel: 'Abril 2027 – Junio 2027', mesInicio: '04', diaInicio: '01', mesFin: '06', diaFin: '30' }
];

const parsePathDetails = (path) => {
    const parts = path.split('/');
    const clientesIndex = parts.indexOf('clientes');
    const polizasIndex = parts.indexOf('polizas');
    
    let clienteId = null;
    let polizaId = null;
    
    if (clientesIndex !== -1 && clientesIndex + 1 < parts.length) clienteId = parts[clientesIndex + 1];
    if (polizasIndex !== -1 && polizasIndex + 1 < parts.length) polizaId = parts[polizasIndex + 1];
    
    return { clienteId, polizaId };
};

const calcularEstadoEmision = (estadoEmisionRaw, fInicioRaw, fVencRaw) => {
    let estadoEmisionCalculado = estadoEmisionRaw || 'SIN_ESTADO';
    
    if (!['ANULADA', 'CANCELADA', 'RECHAZADA'].includes(String(estadoEmisionCalculado).toUpperCase())) {
        let hoyDate = new Date();
        if (hoyDate.getFullYear() < 2026) {
            hoyDate = parseDateToComparable('2026-08-19');
        }

        const fInicioDate = parseDateToComparable(fInicioRaw);
        const fVencDate = parseDateToComparable(fVencRaw);

        if (fInicioDate && fVencDate && hoyDate) {
            hoyDate.setHours(0, 0, 0, 0);
            fInicioDate.setHours(0, 0, 0, 0);
            fVencDate.setHours(0, 0, 0, 0);

            if (hoyDate < fInicioDate) {
                estadoEmisionCalculado = 'NO INICIADA';
            } else if (hoyDate >= fInicioDate && hoyDate <= fVencDate) {
                estadoEmisionCalculado = 'VIGENTE';
            } else {
                estadoEmisionCalculado = 'VENCIDA';
            }
        }
    }
    return estadoEmisionCalculado;
};

export const useObjetivosSancor = () => {
    const { dbMasters } = useSegurosData();
    const { 
        objetivosSancorCacheMap, 
        setObjetivosSancorCacheMap, 
        loadingObjetivosSancor, 
        setLoadingObjetivosSancor 
    } = useReportesMetrics();

    const [selectedCuatrimestreId, setSelectedCuatrimestreId] = useState('Q1');
    const [selectedOrganizador, setSelectedOrganizador] = useState('');
    const [selectedPas, setSelectedPas] = useState('');

    const [isDbLoaded, setIsDbLoaded] = useState(false);
    const [error, setError] = useState(null);

    const cacheEntry = objetivosSancorCacheMap[selectedCuatrimestreId];
    const polizasSancor = cacheEntry?.data || [];

    const codigosSancorData = useMemo(() => {
        const companias = dbMasters?.companias || [];
        const sancorDoc = companias.find(c => {
            const nombre = String(c.nombre || c.razonSocial || c.id || '').toUpperCase();
            return nombre.includes('SANCOR');
        });

        const codigosList = sancorDoc?.codigos || [];
        
        const organizadoresSet = new Set();
        codigosList.forEach(item => {
            if (item.organizador) organizadoresSet.add(String(item.organizador).trim());
        });

        const organizadoresUnicos = [...organizadoresSet].sort();

        const pasFiltrados = codigosList
            .filter(item => !selectedOrganizador || item.organizador === selectedOrganizador)
            .map(item => item.pas)
            .filter(Boolean);

        const pasUnicos = [...new Set(pasFiltrados)].sort();

        return { codigosList, organizadoresUnicos, pasUnicos };
    }, [dbMasters?.companias, selectedOrganizador]);

    // METAS TRIMESTRALES SEGÚN CÓDIGO
    const metasConfig = useMemo(() => {
        const orgLimpio = String(selectedOrganizador || '').trim();
        const pasLimpio = String(selectedPas || '').trim();

        const esMarcos = orgLimpio.includes('211380') || pasLimpio.includes('211380');
        const esMaxiORebeca = 
            (orgLimpio.includes('151497') || !orgLimpio) && 
            (pasLimpio.includes('232064') || pasLimpio.includes('233823'));

        if (esMarcos) {
            return {
                isAgrupadoAutoMoto: false,
                autosCamion: { total: 70, nuevas: 35, renovaciones: 35 },
                motos: { total: 30, nuevas: 15, renovaciones: 15 },
                hogar: { total: 90, nuevas: 45, renovaciones: 45 },
                comercio: { total: 15, nuevas: 8, renovaciones: 7 },
                salud: { total: 138, nuevas: 138, renovaciones: 0 },
                caucion: { total: 15, nuevas: 15, renovaciones: 0 },
                ap: { total: 200, nuevas: 100, renovaciones: 100 },
                responsabilidadCivil: null
            };
        }

        if (esMaxiORebeca) {
            return {
                isAgrupadoAutoMoto: false,
                autosCamion: { total: 25, nuevas: 13, renovaciones: 12 },
                motos: { total: 15, nuevas: 8, renovaciones: 7 },
                hogar: { total: 10, nuevas: 5, renovaciones: 5 },
                comercio: { total: 10, nuevas: 5, renovaciones: 5 },
                salud: { total: 10, nuevas: 10, renovaciones: 0 },
                caucion: { total: 5, nuevas: 5, renovaciones: 0 },
                ap: { total: 100, nuevas: 50, renovaciones: 50 },
                responsabilidadCivil: null
            };
        }

        return {
            isAgrupadoAutoMoto: true,
            autosMotos: { total: 150, nuevas: 75, renovaciones: 75 },
            hogar: { total: 88, nuevas: 44, renovaciones: 44 },
            comercio: { total: 13, nuevas: 7, renovaciones: 6 },
            salud: { total: 138, nuevas: 138, renovaciones: 0 },
            caucion: { total: 13, nuevas: 13, renovaciones: 0 },
            ap: { total: 200, nuevas: 100, renovaciones: 100 },
            responsabilidadCivil: { total: 30, nuevas: 15, renovaciones: 15 }
        };
    }, [selectedOrganizador, selectedPas]);

    const currentCuatrimestreConfig = useMemo(() => {
        return TRIMESTRES_CONFIG.find(c => c.id === selectedCuatrimestreId) || TRIMESTRES_CONFIG[0];
    }, [selectedCuatrimestreId]);

    const rangoFechasCuatrimestre = useMemo(() => {
        let yearInicio = '2026';
        let yearFin = '2026';
        if (selectedCuatrimestreId === 'Q3' || selectedCuatrimestreId === 'Q4') {
            yearInicio = '2027';
            yearFin = '2027';
        } 

        const desde = `${yearInicio}-${currentCuatrimestreConfig.mesInicio}-${currentCuatrimestreConfig.diaInicio}`;
        const hasta = `${yearFin}-${currentCuatrimestreConfig.mesFin}-${currentCuatrimestreConfig.diaFin}`;

        return { desde, hasta };
    }, [currentCuatrimestreConfig, selectedCuatrimestreId]);

    const cargarDatosSancor = useCallback(async (forceRefresh = false) => {
        const now = Date.now();
        const currentEntry = objetivosSancorCacheMap[selectedCuatrimestreId];
        const lastFetch = currentEntry?.lastFetch || 0;
        const isCacheValid = (now - lastFetch) < CACHE_TTL_MS;

        // Si la caché es válida y no se forzó actualización manual, retoma datos en RAM
        if (!forceRefresh && currentEntry?.data && isCacheValid) {
            setIsDbLoaded(true);
            setLoadingObjetivosSancor(false);
            return;
        }

        // 🌟 FORZAMOS ESTADO DE CARGA INMEDIATO PARA ACTIVAR SKELETON
        setLoadingObjetivosSancor(true);
        setIsDbLoaded(false);
        setError(null);

        try {
            const { desde, hasta } = rangoFechasCuatrimestre;

            const qCarga = query(
                collectionGroup(db, 'polizas'),
                where('fechaCarga', '>=', desde),
                where('fechaCarga', '<=', hasta)
            );

            const qAnulacion = query(
                collectionGroup(db, 'polizas'),
                where('fechaAnulacion', '>=', desde),
                where('fechaAnulacion', '<=', hasta)
            );

            const [snapCarga, snapAnulacion] = await Promise.all([
                getDocs(qCarga),
                getDocs(qAnulacion)
            ]);

            const docsMap = new Map();
            snapCarga.docs.forEach(docSnap => docsMap.set(docSnap.id, docSnap));
            snapAnulacion.docs.forEach(docSnap => docsMap.set(docSnap.id, docSnap));

            if (docsMap.size === 0) {
                setObjetivosSancorCacheMap(prev => ({ 
                    ...prev, 
                    [selectedCuatrimestreId]: { data: [], lastFetch: Date.now() } 
                }));
                setIsDbLoaded(true);
                setLoadingObjetivosSancor(false);
                return;
            }

            const rows = Array.from(docsMap.values()).map(docSnap => {
                const data = docSnap.data();
                const { clienteId, polizaId } = parsePathDetails(docSnap.ref.path);

                const fInicio = data.inicioVigencia || data.fechaInicio;
                const fVenc = data.finVigencia || data.fechaVencimiento;
                const estadoEmisionCalculado = calcularEstadoEmision(data.estadoEmision, fInicio, fVenc);

                const primaEmitidaClean = Math.abs(parseCurrency(data.primaEmitida || data.premio || 0));
                const primaAnulacionRaw = data.primaAnulacion !== undefined && data.primaAnulacion !== null 
                    ? data.primaAnulacion 
                    : 0;
                const primaAnulacionClean = Math.abs(parseCurrency(primaAnulacionRaw));

                const ape = String(data.clienteApellido || data.apellido || '').trim();
                const nom = String(data.clienteNombre || data.nombre || '').trim();
                let clienteCompleto = `${ape} ${nom}`.trim();
                if (!clienteCompleto) {
                    clienteCompleto = String(data.cliente || data.asegurado || data.razonSocial || 'CLIENTE SANCOR').trim();
                }

                return {
                    id: docSnap.id,
                    polizaId,
                    clienteId,
                    clienteNombre: clienteCompleto.toUpperCase(),
                    compania: String(data.compania || '').trim().toUpperCase(),
                    riesgo: String(data.riesgo || '').trim().toUpperCase(),
                    subriesgo: String(data.subriesgo || '').trim().toUpperCase(),
                    motivo: String(data.motivo || data.tipoOperacion || '').trim().toUpperCase(),
                    subOperacion: String(data.subOperacion || '').trim().toUpperCase(),
                    estadoEmision: estadoEmisionCalculado,
                    isPolizaPack: data.isPolizaPack === true || data.isPolizaPack === 'true' || data.isPack === true,
                    primaEmitida: primaEmitidaClean,
                    primaAnulacion: primaAnulacionClean,
                    moneda: data.moneda || '$',
                    fechaCarga: formatDateForInput(data.fechaCarga),
                    inicioVigencia: formatDateForInput(fInicio),
                    finVigencia: formatDateForInput(fVenc),
                    fechaAnulacion: formatDateForInput(data.fechaAnulacion),
                    vendedor: String(data.vendedor || '').trim(),
                    quienRenovo: String(data.quienRenovo || '').trim(),
                    nPoliza: data.nPoliza || '',
                    organizador: String(data.organizador || '').trim(),
                    pas: String(data.pas || '').trim()
                };
            }).filter(item => item.compania.includes('SANCOR'));

            setObjetivosSancorCacheMap(prev => ({
                ...prev,
                [selectedCuatrimestreId]: {
                    data: rows,
                    lastFetch: Date.now()
                }
            }));
            setIsDbLoaded(true);
        } catch (err) {
            console.error("Error al cargar datos de Objetivos Sancor:", err);
            setError(err.message || "Error al conectar con Firestore");
            setIsDbLoaded(true);
        } finally {
            setLoadingObjetivosSancor(false);
        }
    }, [rangoFechasCuatrimestre, selectedCuatrimestreId, objetivosSancorCacheMap, setObjetivosSancorCacheMap, setLoadingObjetivosSancor]);

    useEffect(() => {
        cargarDatosSancor(false);
    }, [selectedCuatrimestreId]);

    const metrics = useMemo(() => {
        const resetRamo = () => ({ altas: 0, bajas: 0, neto: 0, nuevas: 0, renovaciones: 0 });

        const acumulador = {
            autosMotos: resetRamo(),
            autosCamion: resetRamo(),
            motos: resetRamo(),
            hogar: resetRamo(),
            comercio: resetRamo(),
            ap: resetRamo(),
            salud: resetRamo(),
            caucion: resetRamo(),
            responsabilidadCivil: resetRamo()
        };

        let autosSoloNeto = 0;
        let motosSoloNeto = 0;
        let camionesSoloNeto = 0;

        let totalCrossSellingHogar = 0;
        let totalCambioCompaniaCount = 0;
        let totalPrimaEmitida = 0;
        let totalPrimaAnulacion = 0;

        const { desde: desdeCuatri, hasta: hastaCuatri } = rangoFechasCuatrimestre;
        const colaboradoresMap = {};
        const listadoAltas = [];
        const listadoBajas = [];

        polizasSancor.forEach(item => {
            if (selectedOrganizador && !item.organizador.includes(selectedOrganizador)) return;
            if (selectedPas && !item.pas.includes(selectedPas)) return;

            const riesgo = item.riesgo;
            const estadoEmision = item.estadoEmision;
            const motivo = item.motivo;
            const subOperacion = item.subOperacion;

            const esAuto = riesgo.includes('AUTOMOTORES');
            const esMoto = riesgo.includes('MOTOVEHÍCULOS') || riesgo.includes('MOTOVEHICULOS');
            const esCamion = riesgo === 'CAMIÓ' || riesgo === 'CAMION' || riesgo.includes('CAMIÓN') || riesgo.includes('CAMION');
            
            const esAutoOCamion = esAuto || esCamion;
            const esAutoMotosGlobal = esAuto || esMoto || esCamion;

            const esHogar = riesgo.includes('COMBINADO FAMILIAR');
            const esComercio = riesgo.includes('INTEGRAL DE COMERCIO');
            const esAP = riesgo.includes('ACCIDENTES PERSONALES');
            const esSalud = riesgo.includes('SALUD');
            const esCaucion = riesgo.includes('CAUCION') || riesgo.includes('CAUCIÓN');
            const esRC = riesgo.includes('RESPONSABILIDAD CIVIL');

            if (!esAutoMotosGlobal && !esHogar && !esComercio && !esAP && !esSalud && !esCaucion && !esRC) return;

            const fueAnuladoEnCuatri = item.fechaAnulacion && item.fechaAnulacion >= desdeCuatri && item.fechaAnulacion <= hastaCuatri;
            const esBaja = (estadoEmision === 'ANULADA' || !!item.fechaAnulacion) && fueAnuladoEnCuatri;

            const esEmisionNueva = 
                (motivo.includes('PÓLIZA NUEVA') || motivo.includes('POLIZA NUEVA') || motivo.includes('NUEVA') || motivo.includes('EMISION')) &&
                (subOperacion.includes('EMISIÓN NUEVA') || subOperacion.includes('EMISION NUEVA') || subOperacion === '');

            const esPaseCompania = 
                (motivo.includes('RENOVACION') || motivo.includes('RENOVACIÓN')) &&
                (subOperacion.includes('CAMBIO DE COMPAÑIA') || subOperacion.includes('CAMBIO DE COMPAÑÍA'));

            const fueCargadoEnCuatri = item.fechaCarga >= desdeCuatri && item.fechaCarga <= hastaCuatri;
            
            const esAlta = fueCargadoEnCuatri && (esEmisionNueva || esPaseCompania) && (estadoEmision === 'VIGENTE' || estadoEmision === 'NO INICIADA' || estadoEmision === 'VENCIDA');

            let responsable = 'SIN ASIGNAR';
            if (esPaseCompania) {
                responsable = item.quienRenovo || item.vendedor || 'SIN ASIGNAR';
            } else {
                responsable = item.vendedor || 'SIN ASIGNAR';
            }

            const colabKey = responsable.toUpperCase();

            if (!colaboradoresMap[colabKey]) {
                colaboradoresMap[colabKey] = { 
                    nombre: responsable, 
                    autosMotosAltas: 0, autosMotosBajas: 0,
                    autosCamionAltas: 0, autosCamionBajas: 0,
                    motosAltas: 0, motosBajas: 0,
                    hogarAltas: 0, hogarBajas: 0,
                    comercioAltas: 0, comercioBajas: 0,
                    apAltas: 0, apBajas: 0,
                    saludAltas: 0, saludBajas: 0,
                    caucionAltas: 0, caucionBajas: 0,
                    responsabilidadCivilAltas: 0, responsabilidadCivilBajas: 0,
                    polizasAltas: [],
                    polizasBajas: []
                };
            }

            let ramosTarget = [];
            if (esAutoMotosGlobal) ramosTarget.push('autosMotos');
            if (esAutoOCamion) ramosTarget.push('autosCamion');
            if (esMoto) ramosTarget.push('motos');
            if (esHogar) ramosTarget.push('hogar');
            if (esComercio) ramosTarget.push('comercio');
            if (esAP) ramosTarget.push('ap');
            if (esSalud) ramosTarget.push('salud');
            if (esCaucion) ramosTarget.push('caucion');
            if (esRC) ramosTarget.push('responsabilidadCivil');

            if (esAlta) {
                listadoAltas.push(item);
                colaboradoresMap[colabKey].polizasAltas.push(item);
                totalPrimaEmitida += item.primaEmitida;

                if (esPaseCompania) totalCambioCompaniaCount += 1;

                ramosTarget.forEach(r => {
                    acumulador[r].altas += 1;
                    if (esPaseCompania) acumulador[r].renovaciones += 1;
                    else acumulador[r].nuevas += 1;

                    colaboradoresMap[colabKey][`${r}Altas`] += 1;
                });

                if (esAuto) autosSoloNeto += 1;
                if (esMoto) motosSoloNeto += 1;
                if (esCamion) camionesSoloNeto += 1;

                if (esHogar && item.isPolizaPack) totalCrossSellingHogar += 1;

            } else if (esBaja) {
                listadoBajas.push(item);
                colaboradoresMap[colabKey].polizasBajas.push(item);
                totalPrimaAnulacion += item.primaAnulacion;

                ramosTarget.forEach(r => {
                    acumulador[r].bajas += 1;
                    colaboradoresMap[colabKey][`${r}Bajas`] += 1;
                });

                if (esAuto) autosSoloNeto -= 1;
                if (esMoto) motosSoloNeto -= 1;
                if (esCamion) camionesSoloNeto -= 1;
            }
        });

        Object.keys(acumulador).forEach(k => {
            acumulador[k].neto = acumulador[k].altas - acumulador[k].bajas;
        });

        const calcPorcentaje = (neto, meta) => meta > 0 ? Math.min(Math.max(Math.round((neto / meta) * 100), 0), 100) : 0;

        const riesgosFinales = {};
        Object.keys(metasConfig).forEach(k => {
            if (k === 'isAgrupadoAutoMoto') return;

            const metaObs = metasConfig[k];
            if (!metaObs) {
                riesgosFinales[k] = null;
                return;
            }

            const obs = acumulador[k];
            riesgosFinales[k] = {
                ...obs,
                metaCuatri: metaObs.total,
                metaNuevas: metaObs.nuevas,
                metaRenovaciones: metaObs.renovaciones,
                porcCuatri: calcPorcentaje(obs.neto, metaObs.total)
            };
        });

        if (metasConfig.isAgrupadoAutoMoto && riesgosFinales.autosMotos) {
            riesgosFinales.autosMotos.autosSoloNeto = autosSoloNeto;
            riesgosFinales.autosMotos.motosSoloNeto = motosSoloNeto;
            riesgosFinales.autosMotos.camionesSoloNeto = camionesSoloNeto;
        }

        const totalEmitidasCount = listadoAltas.length;
        const totalAnuladasCount = listadoBajas.length;

        const ratioCrossSelling = acumulador.hogar.altas > 0 ? ((totalCrossSellingHogar / acumulador.hogar.altas) * 100).toFixed(1) : 0;

        const desgloseOperadores = Object.values(colaboradoresMap).map(op => {
            const netoAutosMotos = op.autosMotosAltas - op.autosMotosBajas;
            const netoAutosCamion = op.autosCamionAltas - op.autosCamionBajas;
            const netoMotos = op.motosAltas - op.motosBajas;
            const netoHogar = op.hogarAltas - op.hogarBajas;
            const netoComercio = op.comercioAltas - op.comercioBajas;
            const netoAP = op.apAltas - op.apBajas;
            const netoSalud = op.saludAltas - op.saludBajas;
            const netoCaucion = op.caucionAltas - op.caucionBajas;
            const netoRC = op.responsabilidadCivilAltas - op.responsabilidadCivilBajas;

            const totalAltas = op.autosMotosAltas + op.hogarAltas + op.comercioAltas + op.apAltas + op.saludAltas + op.caucionAltas + op.responsabilidadCivilAltas;
            const totalBajas = op.autosMotosBajas + op.hogarBajas + op.comercioBajas + op.apBajas + op.saludBajas + op.caucionBajas + op.responsabilidadCivilBajas;
            const totalNeto = totalAltas - totalBajas;

            return { 
                ...op, 
                netoAutosMotos,
                netoAutosCamion,
                netoMotos,
                netoHogar, 
                netoComercio, 
                netoAP, 
                netoSalud, 
                netoCaucion, 
                netoRC,
                totalAltas, 
                totalBajas, 
                totalNeto 
            };
        }).sort((a, b) => b.totalNeto - a.totalNeto);

        return {
            isAgrupadoAutoMoto: metasConfig.isAgrupadoAutoMoto,
            riesgos: riesgosFinales,
            totalesGenerales: {
                totalEmitidasCount,
                totalAnuladasCount,
                totalCambioCompaniaCount,
                totalPrimaEmitida,
                totalPrimaAnulacion,
                balancePrimaNeto: totalPrimaEmitida - totalPrimaAnulacion
            },
            crossSelling: {
                totalCross: totalCrossSellingHogar,
                totalHogarAltas: acumulador.hogar.altas,
                ratio: ratioCrossSelling
            },
            desgloseOperadores,
            listadoAltas,
            listadoBajas
        };
    }, [polizasSancor, rangoFechasCuatrimestre, selectedOrganizador, selectedPas, metasConfig]);

    return {
        selectedCuatrimestreId,
        setSelectedCuatrimestreId,
        selectedOrganizador,
        setSelectedOrganizador,
        selectedPas,
        setSelectedPas,
        codigosSancorData,
        cuatrimestresList: TRIMESTRES_CONFIG,
        currentCuatrimestreConfig,
        rangoFechasCuatrimestre,
        loading: loadingObjetivosSancor,
        isDbLoaded,
        error,
        metrics,
        refetch: () => cargarDatosSancor(true) // Forzado manual
    };
};